import * as React from "react"
import { useCallback, useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"

import { VBox, Spacer, HBox, Icon, Code } from "@molnx-sites/common/components"
import classNames from "@molnx-sites/common/utils/classNames"

import { H2, H3 } from "src/components"

import * as OA from "./OpenApiSpecification"

import styles from "./ApiReference.module.scss"


export function ApiReference(props: {
    title: string
    specification: OA.Specification
}) {
    const {
        specification
    } = props

    return (
        <VBox className={styles.Root}>
            <H2>Endpoints</H2>
            <EndpointsList paths={specification.paths} specification={specification}/>
            <H2>Models</H2>
            <ModelsList schemas={props.specification?.components?.schemas ?? {}} specification={specification}/>
        </VBox>
    )
}

function EndpointsList(props: {
    paths: { [pathName: string]: OA.Path }
    specification: OA.Specification
}) {
    return (
        <div>
            {Object.entries(props.paths).map(([key, data]) => {
                return <Path key={key} pathName={key} path={data} specification={props.specification}/>
            })}
        </div>
    )
}

function Path(props: {
    pathName: string
    path: OA.Path
    specification: OA.Specification
}) {
    return (
        <div>
            {Object.entries(props.path).map(([methodName, method]) => {
                return <Method
                    key={`${props.pathName}-${methodName}`}
                    pathName={props.pathName}
                    methodName={methodName}
                    method={method}
                    specification={props.specification}
                />
            })}
        </div>
    )
}

function Method(props: {
    pathName: string,
    methodName: string,
    method: OA.Method
    specification: OA.Specification
}) {
    const {
        pathName,
        methodName,
        method,
        specification
    } = props

    const headerText = `${methodName.toUpperCase()} ${pathName}`
    const id = `endpoint-${headerText}`.replace(" ", "-")
    const hash = `#${id}`

    const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined)
    const onChange = useCallback((next: boolean) => setIsOpen(next), [setIsOpen])

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            // Expand on any matching link clicks even if the user has already closed it once.
            // We need this because clicking on a link will not trigger the "hashchange" event
            // if the hash doesn't change.
            if ((e.target as any)?.href?.endsWith(hash)) {
                setIsOpen(true)
            }
        }

        if (window.location.hash === hash) {
            setIsOpen(true)
        }

        window.addEventListener("click", onClick)
        return () => window.removeEventListener("click", onClick)
    }, [hash, setIsOpen])

    return (
        <ExpandableCard
            isOpen={isOpen}
            onChange={onChange}
            header={
                <H3 text={headerText} id={id}>
                    <MethodNameText name={methodName}/>
                    <MethodPathText path={pathName}/>
                    <MethodSummaryText summary={method.summary}/>
                </H3>
            }
        >
            <SecurityRequirementsSection security={method.security}/>
            {md(method.description)}
            <PathParametersSection parameters={method.parameters} specification={specification}/>
            <QueryParametersSection parameters={method.parameters} specification={specification}/>
            <RequestBodySection requestBody={method.requestBody} specification={specification}/>
            <ResponsesSection responses={method.responses} specification={specification}/>
            <ErrorsSection errors={method["x-errors"]} specification={specification}/>
        </ExpandableCard>
    )
}

function MethodNameText(props: { name: string }) {
    const className = classNames(styles.MethodNameText, props.name)
    return <span className={className}>{props.name}</span>
}

function MethodPathText(props: { path: string }) {
    return <span className={styles.MethodPathText}>{props.path}</span>
}

function MethodSummaryText(props: { summary: string }) {
    return <span className={styles.MethodSummaryText}>{md(props.summary, { noRoot: true })}</span>
}

function SecurityRequirementsSection(props: { security: OA.SecurityRequirement[] | undefined }) {
    const { security } = props
    if (!security || security.length === 0) {
        return null
    }

    return (
        <div className={styles.SecurityRequirementsSection}>
            {
                security.map(requirement => Object.entries(requirement).map(([name, scopes]) => {
                    return (
                        <p key={name} className={styles.SecurityRequirementsScopes}>
                            {scopes.map(scope => <span key={scope}
                                                       className={styles.SecurityRequirementsScope}>{scope}</span>)}
                        </p>
                    )
                }))}
        </div>
    )
}

function RequestBodySection(props: {
    requestBody: OA.Body | undefined
    specification: OA.Specification
}) {
    if (!props.requestBody) {
        return null
    }

    const schema = props.requestBody?.content?.["application/json"]?.schema
    const properties = schema?.properties ?? []
    const required = schema?.required ?? []

    return (
        <div className={styles.Section}>
            <h4>Request body</h4>
            <table className={styles.ParametersTable}>
                <tbody>
                {Object.entries(properties).map(([name, schema]) => {
                    const resolvedSchema = OA.resolveSchema(schema, props.specification)

                    return <ParameterRow
                        key={name}
                        description={resolvedSchema.description}
                        name={name}
                        schema={resolvedSchema}
                        required={required.includes(name)}
                        specification={props.specification}
                    />
                })
                }
                </tbody>
            </table>
        </div>
    )
}

function PathParametersSection(props: {
    parameters: OA.Parameter[] | undefined
    specification: OA.Specification
}) {
    const pathParameters = props.parameters?.filter(it => it.in === "path") ?? []
    if (pathParameters.length === 0) {
        return null
    }

    return (
        <div className={styles.Section}>
            <h4>Path parameters</h4>
            <ParametersTable parameters={pathParameters} specification={props.specification} hideRequired/>
        </div>
    )
}

function QueryParametersSection(props: {
    parameters: OA.Parameter[] | undefined
    specification: OA.Specification
}) {
    const queryParameters = props.parameters?.filter(it => it.in === "query") ?? []
    if (queryParameters.length === 0) {
        return null
    }

    return (
        <div className={styles.Section}>
            <h4>Query parameters</h4>
            <ParametersTable parameters={queryParameters} specification={props.specification}/>
        </div>
    )
}

function ResponsesSection(props: {
    responses: { [statusCode: string]: OA.Response } | undefined
    specification: OA.Specification
}) {
    if (!props.responses) {
        return null
    }

    return (
        <div className={styles.Section}>
            <h4>Responses</h4>
            <table className={styles.ResponsesTable}>
                <tbody>
                {Object.entries(props.responses).map(([statusCode, response]) => {
                    const contentType = Object.entries(response.content ?? {})[0]?.[0]

                    return (
                        <React.Fragment key={statusCode}>
                            <tr>
                                <td><code className={styles.StatusCodeText}>{statusCode}</code></td>
                                <td>{contentType ? <code>{contentType}</code> : "No Content"}</td>
                                <td>
                                    {md(response.description)}
                                </td>
                            </tr>
                            <ExampleRow
                                contentType={contentType}
                                content={response.content?.[contentType]}
                                specification={props.specification}
                            />
                        </React.Fragment>
                    )
                })}
                </tbody>
            </table>
        </div>
    )
}

function ExampleRow(props: {
    contentType: string
    content: OA.Content | undefined
    specification: OA.Specification
}) {
    const example = OA.resolveExample(props.content?.example, props.specification)
    if (!example) {
        return null
    }

    return (
        <tr>
            <td/>
            <td colSpan={2}>
                <ExampleView contentType={props.contentType} example={example}/>
            </td>
        </tr>
    )
}

function ExampleView(props: {
    contentType: string
    example: OA.ResolvedExample | undefined
}) {
    const {
        contentType,
        example
    } = props

    const value = example?.value
    if (!value) {
        return null
    }

    const parsedValue = contentType === "application/json"
        ? JSON.stringify(value, null, 2)
        : `${value}`

    return (
        <div className={styles.ExampleView}>
            <h5>Example response body</h5>
            <Code>
                {parsedValue}
            </Code>
        </div>
    )
}

function ParametersTable(props: {
    parameters: OA.Parameter[],
    hideRequired?: boolean
    specification: OA.Specification
}) {
    return (
        <table className={styles.ParametersTable}>
            <tbody>
            {props.parameters.map(it => {
                const resolvedSchema = OA.resolveSchema(it.schema, props.specification)
                return <ParameterRow
                    key={it.name}
                    {...it}
                    schema={resolvedSchema}
                    hideRequired={props.hideRequired}
                    specification={props.specification}
                />
            })}
            </tbody>
        </table>
    )
}

function ParameterRow(props: {
    name: string
    description?: string
    schema?: OA.ResolvedSchema
    required?: boolean
    hideRequired?: boolean
    specification: OA.Specification
}) {
    const schema = props.schema
    const description = schema?.description ?? props.description
    const hideRequired = props.hideRequired ?? false
    const isRequired = props.required ?? false

    const exampleValue = schema?.example
        ? OA.resolveExample(schema.example, props.specification)?.value
        : undefined

    return (
        <tr key={props.name}>
            <td>
                <code>{props.name}</code>
            </td>
            <td>
                {!hideRequired && <RequiredIndicator required={isRequired}/>}
            </td>
            <td>
                {schema && <SchemaType schema={schema}/>}
            </td>
            <td>
                {md(description)}
                {exampleValue && <p className={styles.ExampleValue}>Example: <code>{exampleValue}</code></p>}
            </td>
        </tr>
    )
}

function RequiredIndicator(props: { required: boolean }) {
    return props.required
        ? <span className={classNames(styles.RequiredIndicatorText, "Required")}>Required</span>
        : <span className={classNames(styles.RequiredIndicatorText, "Optional")}>Optional</span>
}

function SchemaType(props: { schema: OA.ResolvedSchema }) {
    const schemaTypeNameText = <span
        className={styles.SchemaTypeNameText}>{props.schema.type}{props.schema.nullable ? " | NULL" : null}</span>

    const format = (props.schema as OA.IntegerSchema | OA.StringSchema).format
    const formatText = format
        ? <span className={styles.SchemaTypeFormatText}>{getPrettyTypeFormat(format)}</span>
        : null

    const typeLimitsText = OA.hasLimits(props.schema)
        ? <span className={styles.SchemaTypeLimitsText}>{getPrettyTypeLimits(props.schema)}</span>
        : null

    const enumText = OA.isStringSchema(props.schema) && props.schema.enum
        ? <span className={styles.SchemaTypeEnumText}>{props.schema.enum.map(it => <>{it}<br/></>)}</span>
        : null

    return (
        <div className={styles.SchemaType}>
            {schemaTypeNameText}
            {formatText && (<><br/>{formatText}</>)}
            {typeLimitsText && (<><br/>{typeLimitsText}</>)}
            {enumText && (<><br/>{enumText}</>)}
        </div>
    )
}

function ErrorsSection(props: {
    errors?: string[]
    specification: OA.Specification
}) {
    const errors = props.errors?.map(code => {
        const definition = props.specification.components?.["x-errorDefinitions"]?.[code]
        return { code, ...definition! }
    }) ?? []

    if (errors.length === 0) {
        return null
    }

    return (
        <div className={styles.Section}>
            <h4>Error codes</h4>
            <table className={styles.ErrorCodesTable}>
                <tbody>
                {errors.map(it => (
                    <tr>
                        <td><code>{it.code}</code></td>
                        <td>{md(it.description)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    )
}

function getPrettyTypeFormat(format: OA.AllFormats) {
    switch (format) {
        case "date-time":
            return "ISO-8601 Date & Time"
        default:
            return format
    }
}

function getPrettyTypeLimits(schema: OA.ResolvedSchema) {
    const defaultValue = schema.default
    const defaultSuffix = defaultValue ? ` (Default: ${defaultValue})` : ""

    if (OA.isIntegerSchema(schema)) {
        const { minimum, maximum } = schema
        if (minimum !== undefined && maximum !== undefined) {
            return `${minimum} - ${maximum}${defaultSuffix}`
        } else if (minimum !== undefined) {
            return `> ${minimum}${defaultSuffix}`
        } else if (maximum !== undefined) {
            return `< ${maximum}${defaultSuffix}`
        } else {
            return defaultSuffix
        }
    } else if (OA.isStringSchema(schema)) {
        const { minLength, maxLength } = schema
        if (minLength !== undefined && maxLength !== undefined) {
            return `${minLength} - ${maxLength}${defaultSuffix}`
        } else if (minLength !== undefined) {
            return `> ${minLength}${defaultSuffix}`
        } else if (maxLength !== undefined) {
            return `< ${maxLength}${defaultSuffix}`
        } else {
            return defaultSuffix
        }
    } else {
        return defaultSuffix
    }
}

function ModelsList(props: {
    schemas: { [modelName: string]: OA.Schema }
    specification: OA.Specification
}) {
    return (
        <div>
            {Object.entries(props.schemas).map(([modelName, schema]) => (
                <ModelSection
                    modelName={modelName}
                    schema={OA.resolveSchema(schema, props.specification)}
                    specification={props.specification}
                />
            ))}
        </div>
    )
}

function ModelSection(props: {
    modelName: string
    schema: OA.ResolvedSchema
    specification: OA.Specification
}) {
    const { modelName, schema, specification } = props
    const id = `model-${modelName}`
    const hash = `#${id}`

    const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined)
    const onChange = useCallback((next: boolean) => setIsOpen(next), [setIsOpen])

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            // Expand on any matching link clicks even if the user has already closed it once.
            // We need this because clicking on a link will not trigger the "hashchange" event
            // if the hash doesn't change.
            if ((e.target as any)?.href?.endsWith(hash)) {
                setIsOpen(true)
            }
        }

        if (window.location.hash === hash) {
            setIsOpen(true)
        }

        window.addEventListener("click", onClick)
        return () => window.removeEventListener("click", onClick)
    }, [hash, setIsOpen])

    return (
        <ExpandableCard
            isOpen={isOpen}
            onChange={onChange}
            header={<H3 text={modelName} id={`model-${modelName}`}>{modelName}</H3>}
        >
            {md(schema.description)}
            <table className={styles.ParametersTable}>
                <tbody>
                {Object.entries((schema as OA.ObjectSchema).properties ?? {}).map(([propertyName, propertySchema]) => {
                    const resolvedPropertySchema = OA.resolveSchema(propertySchema, specification)
                    return (
                        <ParameterRow
                            key={propertyName}
                            description={resolvedPropertySchema?.description}
                            name={propertyName}
                            schema={resolvedPropertySchema}
                            specification={props.specification}
                            hideRequired
                        />
                    )
                })}
                </tbody>
            </table>
        </ExpandableCard>
    )
}

function ExpandableCard(props: {
    header: any
    isOpen?: boolean
    onChange?: (next: boolean) => void
    children: any
}) {
    const {
        header,
        isOpen: controlledIsOpen,
        onChange,
        children,
    } = props

    const [isOpen, setIsOpen] = useState(false)

    const realIsOpen = controlledIsOpen !== undefined ? controlledIsOpen : isOpen

    const onToggle = useCallback(() => {
        if (controlledIsOpen !== undefined) {
            onChange?.(!controlledIsOpen)
        } else {
            const newValue = !isOpen
            setIsOpen(newValue)
            onChange?.(newValue)
        }
    }, [controlledIsOpen, onChange, setIsOpen, controlledIsOpen, isOpen])

    return (
        <div className={styles.ExpandableCard}>
            <HBox onClick={onToggle} style={{ cursor: "pointer", alignItems: "center" }}>
                {header}
                <Spacer/>
                <Icon solid={realIsOpen ? "chevron-right" : "chevron-down"} size="lg"/>
            </HBox>
            <div style={{ display: realIsOpen ? "block" : "none" }}>
                {children}
            </div>
        </div>
    )
}

function md(content: string | undefined, options?: { noRoot?: boolean }) {
    return <ReactMarkdown source={content} renderers={{
        root: (props: any) => {
            if (options?.noRoot) {
                if (props.children?.length !== 1) {
                    throw new Error("Cannot remove root element in markdown sections with multiple elements.")
                } else {
                    return <>{props.children[0].props.children}</>
                }
            } else {
                return props.children
            }
        }
    }}/>
}